import { Trans } from 'react-i18next';
import { useInfluencerPermissions, useUserRoles } from '@anytag/auth';
import {
  SocialAccountType,
  SocialAccountStatus,
  useGetInfluencerRateCardQuery,
  useRequestInfluencersProfilePptxMutation,
  InfluencerNewProfileHeaderV2
} from '@anytag/graphql';
import { Button, ButtonLink, Form, SocialIcon, TextCutter } from '@anytag/shared-atoms';
import { Badge } from '@anyui/core';
import { useQueryHelper, useTranslateCountry } from '@anytag/shared-hooks';
import { InfluencerAvatar } from '@anytag/shared-molecules';
import { bigIntFormatter, formatPrice, getGenderString } from '@anytag/shared-utils';
import { PostPrices } from './PostPrices';
import { SocialMediasWithNumber } from './SocialMediasWithNumber';
import { Tags } from './Tags';
import { getSnsFollowerOptions } from './utils';

export interface InfluencerProfileIntroProps {
  influencerProfile: InfluencerNewProfileHeaderV2;
  objectFit?: 'contain' | 'cover';
  isEngagementProposal?: boolean;
}

export const InfluencerProfileIntro = ({ influencerProfile, isEngagementProposal }: InfluencerProfileIntroProps) => {
  const { isAdvertiser, isAgency } = useUserRoles();
  const { id, age, tags, gender, isTaggedToCompany, country, name, status, avatar, followersCount, isBrandAccount } =
    influencerProfile;
  // https://bitbucket.org/adasiaholdings/casting_asia_advertiser_frontend/pull-requests/2474
  const canEditProfile = isEngagementProposal
    ? isAdvertiser || isAgency
      ? false
      : isTaggedToCompany !== false
    : isTaggedToCompany !== false;

  const { tc } = useTranslateCountry();
  const { t, enqueueSnackbar } = useQueryHelper();
  const { hideEditInfluencerBtn } = useInfluencerPermissions();
  const { data } = useGetInfluencerRateCardQuery({ skip: !canEditProfile, variables: { input: { influencerId: id } } });
  const { loading, callRequestInfluencersProfilePptx } = useRequestInfluencersProfilePptxMutation();

  const rateCard = data?.getInfluencerRateCard;
  const douyinRateCard = rateCard?.douyin;
  const xhsRateCard = rateCard?.xhs;
  const isSignedUp = status === SocialAccountStatus.SIGNED_UP;

  const handleDownload = async () => {
    try {
      await callRequestInfluencersProfilePptx({
        variables: { format: 'PPTX', influencerIds: [id], socialMedias: Object.keys(SocialAccountType) }
      });
      enqueueSnackbar(t('succeededInRequest'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('failedToDownload'), { variant: 'error' });
    }
  };

  return (
    <div className="box-border flex h-max w-[var(--influencer-profile-intro-width)] flex-none flex-col rounded-sm bg-white px-4 pt-6 pb-4 text-primary shadow-container">
      <div className="flex flex-col items-center justify-center">
        <InfluencerAvatar src={avatar} className="rounded-full" size="124px" objectFit="cover" />
        <TextCutter text={name} className="mt-4 text-2xl font-semibold" />
        {isSignedUp && (
          <Badge color="green" className="w-max-content mt-2 h-4.5 rounded-[35px] px-2 text-[10px]">
            {t('Signed Up')}
          </Badge>
        )}
        {isBrandAccount && (
          <Badge color="zinc" className="w-max-content mt-2 h-4.5 rounded-[35px] px-2 text-[10px]">
            {t('Option.Brand')}
          </Badge>
        )}

        <div className="mt-4 flex items-baseline justify-center gap-1 text-base font-normal">
          {age ? (
            <Trans
              values={{ age }}
              i18nKey="ageWithYearsOld"
              components={{ span: <span className="text-sm text-tertiary" /> }}
            />
          ) : (
            '-'
          )}
          <TextCutter text={`${t(`Option.${getGenderString(gender)}`)} ${tc(country.name)}`} />
        </div>
      </div>

      <Form.Line className="my-4" />
      <div className="flex items-center justify-between">
        <div className="text-base font-semibold">{t('General.Followers')}</div>
        <div className="text-xl font-semibold">{bigIntFormatter(followersCount)}</div>
      </div>
      <SocialMediasWithNumber
        currency={rateCard?.currencyId}
        data={getSnsFollowerOptions(influencerProfile)}
        className="mt-4"
      />

      <Form.Line className="my-4" />
      <Tags tags={tags} influencerId={id} />

      {canEditProfile ? (
        <>
          <Form.Line className="my-4" />
          <PostPrices influencerId={id} data={rateCard} />

          {influencerProfile.douyinAccounts?.length && douyinRateCard?.length ? (
            <div className="mt-4 text-sm leading-none">
              <div className="flex items-center">
                <div className="font-semibold">{t('Detailed post price for')}</div>
                <SocialIcon type="DOUYIN" size={16} className="ml-auto" />
                {rateCard?.currencyId ? <span>({rateCard?.currencyId})</span> : null}
              </div>
              {douyinRateCard.map((item, index) => (
                <div key={`douyin-details-${index}`} className="mt-2 flex items-center justify-between">
                  <p>{t(item.description)}:</p>
                  <p>{formatPrice(item.price, rateCard?.currencyId)}</p>
                </div>
              ))}
            </div>
          ) : null}
          {influencerProfile.xhsAccounts?.length && xhsRateCard?.length ? (
            <div className="mt-4 text-sm leading-none">
              <div className="flex items-center">
                <div className="font-semibold">{t('Detailed post price for')}</div>
                <SocialIcon type="XHS" size={16} className="ml-auto" />
                {rateCard?.currencyId ? <span>({rateCard?.currencyId})</span> : null}
              </div>
              {xhsRateCard.map((item, index) => (
                <div key={`xhs-details-${index}`} className="mt-2 flex items-center justify-between">
                  <p>{t(item.description)}:</p>
                  <p>{formatPrice(item.price, rateCard?.currencyId)}</p>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      <Form.Line className="my-4" />
      <div className="flex justify-start gap-2">
        {canEditProfile && !hideEditInfluencerBtn ? (
          <ButtonLink
            variant="outline"
            className="flex-1"
            title={t('Button.Edit Profile')}
            to="/influencer/$id"
            params={{ id: id.toString() }}
          />
        ) : null}
        <Button
          variant="outline"
          loading={loading}
          onClick={handleDownload}
          prefixIcon={{ icon: 'download', size: '14px' }}
          tooltip={{ help: t("Download this influencer's all social accounts") }}
        />
      </div>
    </div>
  );
};
